import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';

import { PaginationResponse } from '../../../shared/models/pagination-response.model';
import * as DrugsListActions from './drugs.actions';
import { DrugService } from './drugs.service';
import {
  selectCurrentPage,
  selectFilters,
  selectPageSize,
  selectSortBy,
  selectIsSortAsc
} from './drugs.selectors';
import { Drug } from './models/drug.model';
import { hasActiveDrugsFilters } from './models/drugs-filter.model';
import { DrugsState } from './drugs.state';

export const loadDrugs$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    drugsListService = inject(DrugService),
    store = inject(Store<DrugsState>)
  ) => {
    return actions$.pipe(
      ofType(
        DrugsListActions.loadDrugs,
        DrugsListActions.setPage,
        DrugsListActions.setPageSize,
        DrugsListActions.setFilters,
        DrugsListActions.setSorting
      ),
      withLatestFrom(
        store.select(selectFilters),
        store.select(selectCurrentPage),
        store.select(selectPageSize),
        store.select(selectSortBy),
        store.select(selectIsSortAsc)
      ),
      filter(([, filters]) => hasActiveDrugsFilters(filters)),
      switchMap(([, filters, currentPage, pageSize, sortBy, isSortAsc]) =>
        drugsListService.getDrugs(filters, currentPage, pageSize, sortBy, isSortAsc).pipe(
          map((response: PaginationResponse<Drug[]>) =>
            DrugsListActions.loadDrugsSuccess({ drugsResponse: response })
          ),
          catchError(error =>
            of(
              DrugsListActions.loadDrugsFailure({
                error: error?.message ?? 'Unknown error loading drugs',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const drugsListEffects: object = { loadDrugs$ };
